import { IMG } from "./ui";

export type Post = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string; // human-readable
  iso: string; // ISO date for metadata
  read: string;
  image: string;
  author: string;
  /** Body blocks: heading or paragraph. */
  body: { type: "h2" | "p"; text: string }[];
};

export const POSTS: Post[] = [
  {
    slug: "doctor-reads-your-chart-first",
    title: "Why the doctor reads your chart before the call",
    excerpt:
      "The best visits start before anyone says hello. Here's how report ingestion writes a pre-visit brief so the AI doctor joins already knowing your story.",
    category: "Product",
    date: "May 28, 2026",
    iso: "2026-05-28",
    read: "6 min read",
    image: IMG.blogChart,
    author: "The WonderfulDoctor team",
    body: [
      { type: "p", text: "Most of the friction in a virtual visit is spent catching the doctor up. You recite your history, dig for last month's lab values, and describe a scan you can't actually see. By the time the real conversation starts, half the time is gone." },
      { type: "h2", text: "Briefed before the room opens" },
      { type: "p", text: "When a patient books, they upload whatever they have — labs, imaging, prior notes. Before the call, our ingestion pipeline extracts the findings and writes a structured pre-visit brief: the relevant values, the open questions, the things worth asking about." },
      { type: "p", text: "That brief is what the AI doctor reads first. It walks into the consultation already oriented, so the patient doesn't spend their fifteen minutes being a medical scribe." },
      { type: "h2", text: "Grounded, not guessing" },
      { type: "p", text: "Every point in the brief cites the report it came from. The doctor can say \"your A1c was 6.4 on the panel from the third\" because that value is sourced — not invented. Imaging is summarized as observations for clinician review, never as a read the machine makes on its own." },
      { type: "p", text: "The result is a visit that opens where it should: with a doctor who has already done the reading, and a patient who gets to talk about what actually matters to them." },
    ],
  },
  {
    slug: "turning-labs-and-scans-into-context",
    title: "Turning labs and scans into clinical context",
    excerpt:
      "A look inside the ingestion pipeline: lab text extraction, multimodal vision for imaging, DICOM, and the embeddings that let the AI doctor retrieve a finding mid-conversation.",
    category: "Engineering",
    date: "May 14, 2026",
    iso: "2026-05-14",
    read: "8 min read",
    image: IMG.blogContext,
    author: "The WonderfulDoctor team",
    body: [
      { type: "p", text: "A patient's reports arrive as a pile of formats: PDF lab panels, scanned notes, imaging studies, the occasional DICOM file. Raw, none of it is usable in a live conversation. The ingestion pipeline turns that pile into clinical context the AI doctor can actually reason over." },
      { type: "h2", text: "Text first" },
      { type: "p", text: "Lab PDFs and notes are run through extraction, then a Claude model on RouterBench structures the values — analyte, result, units, reference range — so a number on page three becomes a fact the doctor can cite." },
      { type: "h2", text: "Vision for imaging, with a hard boundary" },
      { type: "p", text: "Imaging and DICOM go through a multimodal vision pass that produces observations for clinician review. This is deliberate and bounded: the platform describes what's visible to support a clinician's read — it never asserts a diagnostic read of a scan on its own." },
      { type: "h2", text: "Retrieval in the room" },
      { type: "p", text: "Every extracted finding is embedded and indexed. During the visit, when the conversation turns to a symptom or a value, the relevant report is retrieved on the fly — so the doctor's answers stay anchored to the patient's own documents instead of drifting into generalities." },
    ],
  },
  {
    slug: "continuity-of-care-health-journal",
    title: "Continuity of care: the health journal",
    excerpt:
      "An append-only longitudinal record, plus retrieval and memory, so the AI doctor remembers your last visit. And the patient owns it — view, export or delete.",
    category: "Product",
    date: "Apr 30, 2026",
    iso: "2026-04-30",
    read: "7 min read",
    image: IMG.blogContinuity,
    author: "The WonderfulDoctor team",
    body: [
      { type: "p", text: "Care that forgets you isn't care. A visit where you re-explain everything from scratch is the digital version of starting over. The health journal exists so that never happens." },
      { type: "h2", text: "Append-only by design" },
      { type: "p", text: "Every visit, report, medication and vital is added to a longitudinal record that only ever grows. Nothing is quietly overwritten — the history stays intact, so the picture gets richer over time rather than thinner." },
      { type: "h2", text: "Memory the doctor can use" },
      { type: "p", text: "The AI doctor reasons over the journal with retrieval and memory. It can recall that you flagged a side effect two visits ago, or that a value has been trending the wrong way — and bring that into the conversation without being told again." },
      { type: "h2", text: "The patient owns it" },
      { type: "p", text: "It's the patient's record, not ours to lock away. They can view the full journal, export it as FHIR or JSON, or delete it on request. Continuity is the point — but so is control." },
    ],
  },
  {
    slug: "streaming-live-vitals-into-a-visit",
    title: "Streaming live vitals into a consultation",
    excerpt:
      "Heart rate, SpO₂, blood pressure and temperature from BLE devices, HealthKit or manual entry — flowing into the room live, with dangerous readings escalating at once.",
    category: "Engineering",
    date: "Apr 18, 2026",
    iso: "2026-04-18",
    read: "6 min read",
    image: IMG.blogVitals,
    author: "The WonderfulDoctor team",
    body: [
      { type: "p", text: "A consultation that can't see your vitals is working half-blind. So we let the numbers flow into the room as they're taken — not as something the patient reads aloud, but as live context the AI doctor reasons over." },
      { type: "h2", text: "From device to context" },
      { type: "p", text: "Heart rate, SpO₂, blood pressure and temperature stream in from BLE devices, sync from HealthKit, or get entered by hand when that's all the patient has. Each reading updates the doctor's picture mid-conversation." },
      { type: "h2", text: "Dangerous readings don't wait" },
      { type: "p", text: "Some numbers can't be discussed calmly later. An SpO₂ below 90, a blood pressure in crisis range — these trip a red-flag rule the moment they land, raise an urgent banner, and escalate to a human immediately rather than continuing the AI conversation." },
      { type: "p", text: "The goal isn't a wall of telemetry. It's a doctor who notices the one reading that matters and acts on it the instant it appears." },
    ],
  },
  {
    slug: "safety-first-clinician-in-the-loop",
    title: "Safety first: keeping a clinician in the loop",
    excerpt:
      "Information and triage, never an autonomous diagnosis. How red-flag escalation, source-grounded answers, consent and clinician sign-off keep the human in charge.",
    category: "Trust & Safety",
    date: "Apr 2, 2026",
    iso: "2026-04-02",
    read: "7 min read",
    image: IMG.blogSafety,
    author: "The WonderfulDoctor team",
    body: [
      { type: "p", text: "An AI that talks like a doctor has to be bounded like a responsible one. The line we hold is simple: the AI doctor informs and triages — it never makes an autonomous diagnosis or writes a prescription on its own." },
      { type: "h2", text: "Emergencies escalate to a human" },
      { type: "p", text: "Red-flag symptoms and vitals — chest pain, dangerously low oxygen, suicidal ideation — trigger an urgent banner and hand off to a human immediately. The platform never tries to manage an emergency itself or delay real care." },
      { type: "h2", text: "Answers cite their source" },
      { type: "p", text: "Every clinical statement points back to the report or finding behind it. The doctor doesn't assert values it can't support, and imaging is always framed as observations for clinician review rather than a definitive read." },
      { type: "h2", text: "Consent, disclosure and sign-off" },
      { type: "p", text: "Patients always know they're speaking with an AI, and consent for telehealth and recording is captured up front. Where the clinical model requires it, a licensed clinician reviews and signs the visit summary before it reaches the patient." },
    ],
  },
  {
    slug: "pay-as-you-go-explained",
    title: "Pay-as-you-go, explained",
    excerpt:
      "How our pricing works, why a consultation costs what it costs, and how we keep it transparent down to the minute.",
    category: "Company",
    date: "Mar 20, 2026",
    iso: "2026-03-20",
    read: "4 min read",
    image: IMG.blogPricing,
    author: "The WonderfulDoctor team",
    body: [
      { type: "p", text: "Seat-based pricing punishes a clinic for growing and charges it whether or not anyone is seen. We didn't want that, so WonderfulDoctor is pay-as-you-go: you're billed per completed consultation, and nothing else." },
      { type: "h2", text: "Priced by duration" },
      { type: "p", text: "Each consultation's price is set by its length and locked when it's booked — 15 minutes is $17, 30 minutes is $34, 45 minutes is $50.50, and 60 minutes is $67.50 on default rates. That one price covers the AI doctor, report ingestion, the visit summary, scheduling and every patient email." },
      { type: "h2", text: "You only pay when it completes" },
      { type: "p", text: "No-shows and incomplete visits aren't billed. You add a card once — captured securely with Stripe and no charge at capture — and you're billed only when a consultation finishes and its summary is produced. No seats, no subscription, no minimums." },
      { type: "p", text: "We quote the exact price before a visit is ever booked, so there are never surprises on the invoice." },
    ],
  },
];

export function getPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}
